import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyImage component that only loads images when they enter the viewport
 * Uses Intersection Observer API for efficient lazy loading
 */
function LazyImage({ 
  src, 
  alt, 
  style = {}, 
  onError,
  fallbackSrc = '/images/default.jpg',
  placeholder = true,
  rootMargin = '50px', // Start loading 50px before entering viewport
  forceLoad = false, // If true, bypass Intersection Observer and load immediately
  ...props 
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);

    if (forceLoad || !('IntersectionObserver' in window)) {
      setImageSrc(src);
      return;
    }

    const imgElement = imgRef.current;
    if (!imgElement) return;

    const rect = imgElement.getBoundingClientRect();
    const inView =
      rect.bottom >= 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right >= 0 &&
      rect.left <= (window.innerWidth || document.documentElement.clientWidth);

    if (inView) {
      setImageSrc(src);
      return;
    }

    setImageSrc(null);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(src);
            observerRef.current.disconnect();
          }
        });
      },
      {
        rootMargin: rootMargin,
        threshold: 0.01,
      }
    );

    observerRef.current.observe(imgElement);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, rootMargin, forceLoad]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = (e) => {
    setIsLoading(false);
    setHasError(true);
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
    } else if (onError) {
      onError(e);
    }
  };

  // Placeholder styles
  const placeholderStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(100, 100, 100, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.8em',
    zIndex: 1,
  };

  // Extract positioning from style to apply to wrapper if needed
  const isAbsolute = style.position === 'absolute';
  const { position, top, left, right, bottom, width, height, zIndex, ...imgStyle } = style;
  
  const wrapperStyle = isAbsolute 
    ? { 
        position: 'absolute',
        top,
        left,
        right,
        bottom,
        width,
        height,
        zIndex,
      }
    : { 
        position: 'relative',
        width: style.width,
        height: style.height,
      };

  const resolvedOpacity = isAbsolute
    ? (imgStyle.opacity !== undefined ? imgStyle.opacity : 1)
    : (style.opacity !== undefined ? style.opacity : 1);

  // Menus pass placeholder={false} — skip per-image fade so grids do not pop in one-by-one.
  const finalImgStyle = isAbsolute
    ? {
        ...imgStyle,
        width: '100%',
        height: '100%',
        position: 'relative',
        opacity: placeholder && isLoading ? 0.5 : resolvedOpacity,
        transition: placeholder ? 'opacity 0.3s ease' : undefined,
      }
    : {
        ...style,
        opacity: placeholder && isLoading ? 0.5 : resolvedOpacity,
        transition: placeholder ? 'opacity 0.3s ease' : undefined,
      };

  return (
    <div ref={imgRef} style={wrapperStyle}>
      {isLoading && placeholder && !imageSrc && (
        <div style={placeholderStyle}>
          <span>Loading...</span>
        </div>
      )}
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={alt}
          style={finalImgStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
          {...props}
        />
      ) : !placeholder ? (
        <div style={{ width: '100%', height: '100%', backgroundColor: 'rgba(100, 100, 100, 0.2)' }} />
      ) : null}
    </div>
  );
}

export default LazyImage;
